import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { errMsg } from '@/services/staffAttendanceApi';
import { ptApi, splitEmails } from '@/services/ptFilesApi';

const OK = /^[^@\s,;]+@[^@\s,;]+\.[A-Za-z]{2,}$/;

/** Addresses copied (CC) on every PT email — can still be changed for a single send. */
export const PtCcDialog = ({ open, cc, onClose, onSaved }) => {
  const [list, setList] = useState([]);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setList(cc || []); setAdd(''); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const push = () => {
    const vals = splitEmails(add);
    const bad = vals.filter((v) => !OK.test(v));
    if (bad.length) { toast.error(`Not a valid address: ${bad.join(', ')}`); return; }
    const have = new Set(list.map((x) => x.toLowerCase()));
    const fresh = vals.filter((v) => { const k = v.toLowerCase(); if (have.has(k)) return false; have.add(k); return true; });
    setList([...list, ...fresh]);
    setAdd('');
  };
  const save = async () => {
    setBusy(true);
    try { await ptApi.saveCc(list); toast.success('CC addresses saved'); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pt-cc-dialog">
        <DialogHeader>
          <DialogTitle>CC on every PT email</DialogTitle>
          <DialogDescription>These addresses get a copy of each PT file you email. You can still change them for a single send.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={add} onChange={(e) => setAdd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && push()} placeholder="name@ministerwhite.com" data-testid="pt-cc-input" />
          <Button variant="outline" onClick={push} disabled={!add.trim()} data-testid="pt-cc-add"><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="flex min-h-[40px] flex-wrap gap-2" data-testid="pt-cc-list">
          {list.length === 0 ? <span className="text-sm text-gray-400">No CC yet</span> : list.map((e) => (
            <span key={e} className="inline-flex items-center gap-1 rounded-full bg-[#F7F2E7] px-3 py-1 text-sm" data-testid={`pt-cc-chip-${e}`}>{e}
              <button type="button" onClick={() => setList(list.filter((x) => x !== e))} aria-label={`Remove ${e}`} data-testid={`pt-cc-remove-${e}`}><X className="w-3.5 h-3.5 text-gray-500" /></button></span>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="pt-cc-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2D5F4F] hover:bg-[#244c3f]" data-testid="pt-cc-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
